import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageParts";
import { CryptoLogo } from "../components/common/CryptoBits";
import { fmtPrice, fmtCountdown, planState } from "../lib/format";
import { FolderOpen, Trash2, Clock, Trophy, History } from "lucide-react";
import { toast } from "sonner";

export default function OpenPlans() {
  const [tab, setTab] = useState("active");
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);

  const load = () => {
    api.getPlans().then(setPlans).catch(() => {});
    api.getPlanHistory().then(setHistory).catch(() => {});
    api.getPlanHistoryStats().then(setStats).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  const del = async (id) => { await api.deletePlan(id); toast.success("Plan removed"); load(); };

  const total = stats?.total;

  return (
    <div className="ym-fade">
      <PageHeader title="Rencana Aktif" subtitle="Saved plans with live countdown, and a graded history to review win-rate per pair"
        right={
          <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
            <TabBtn active={tab === "active"} onClick={() => setTab("active")} icon={FolderOpen} label={`Active (${plans.length})`} />
            <TabBtn active={tab === "history"} onClick={() => setTab("history")} icon={History} label={`History (${history.length})`} />
          </div>
        } />

      {/* Win-rate summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Stat label="Graded Plans" value={total?.trades ?? 0} />
        <Stat label="Win Rate" value={`${total?.winRate ?? 0}%`} color="var(--green)" />
        <Stat label="Wins" value={total?.wins ?? 0} color="var(--green)" />
        <Stat label="Losses" value={total?.losses ?? 0} color="var(--red)" />
        <Stat label="Total R" value={total ? `${total.totalR >= 0 ? "+" : ""}${total.totalR}R` : "0R"} color={(total?.totalR ?? 0) >= 0 ? "var(--green)" : "var(--red)"} />
      </div>

      {/* Per-pair win rate */}
      {stats?.perPair?.length > 0 && (
        <div className="ym-panel p-4 mb-4">
          <div className="flex items-center gap-2 mb-3"><Trophy size={15} style={{ color: "var(--amber)" }} /><h3 className="text-[13px] font-bold">Win Rate by Pair</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.perPair.map((s) => (
              <div key={s.symbol} className="rounded-lg p-3 flex items-center gap-3" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
                <CryptoLogo base={s.symbol.replace("USDT", "")} size={26} />
                <div className="leading-tight">
                  <div className="font-bold text-[13px]">{s.symbol.replace("USDT", "")}</div>
                  <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>{s.trades} graded · {s.expired} expired</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[15px] font-extrabold tabnum" style={{ color: "var(--green)" }}>{s.winRate}%</div>
                  <div className="text-[10px] tabnum" style={{ color: s.totalR >= 0 ? "var(--green)" : "var(--red)" }}>{s.totalR >= 0 ? "+" : ""}{s.totalR}R</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "active" ? (
        <>
          {!plans.length && <div className="ym-card p-10 text-center" style={{ color: "var(--text-dim)" }}>No active plans. Create one from the Command Center or Trade Planner — it will be graded automatically as price moves.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map((row) => {
              const p = row.plan || {};
              const c = row.direction === "LONG" ? "var(--green)" : row.direction === "SHORT" ? "var(--red)" : "var(--accent-soft)";
              return (
                <div key={row.id} className="ym-card ym-hover p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-[14px]">{row.symbol} <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>{row.interval?.toUpperCase()}</span></div>
                    <button onClick={() => del(row.id)} className="grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
                      <Trash2 size={13} style={{ color: "var(--red)" }} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[18px] font-extrabold" style={{ color: c }}>{row.direction}</span>
                    <span className="pill" style={{ color: "var(--text-muted)", background: "var(--panel-2)", borderColor: "var(--border)" }}>Conf {row.confluence}</span>
                    <span className="pill" style={{ color: planState(p.status).color, background: "var(--panel-2)", borderColor: "var(--border)" }}>{planState(p.status).label}</span>
                  </div>
                  <div className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>{p.datePlan} · {p.timeCreated}</div>
                  <Countdown ts={p.validUntilTs} until={p.validUntil} />
                  {p.entryLow && (
                    <div className="mt-3 space-y-1 text-[12px]">
                      <KV k="Entry" v={`${fmtPrice(p.entryLow)}–${fmtPrice(p.entryHigh)}`} />
                      <KV k="Stop" v={fmtPrice(p.stopLoss)} c="var(--red)" />
                      <KV k="TP1 / TP2 / TP3" v={`${fmtPrice(p.tp1)} / ${fmtPrice(p.tp2)} / ${fmtPrice(p.tp3)}`} c="var(--green)" />
                      <KV k="R:R" v={`1 : ${p.riskReward}`} c="var(--accent-soft)" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {!history.length && <div className="ym-card p-10 text-center" style={{ color: "var(--text-dim)" }}>No graded plans yet. Saved plans move here once a target or stop is hit, or they expire.</div>}
          <div className="space-y-2">
            {history.map((row) => {
              const p = row.plan || {};
              const oc = row.outcome;
              const oColor = oc === "WIN" ? "var(--green)" : oc === "LOSS" ? "var(--red)" : "var(--text-muted)";
              return (
                <div key={row.id} className="ym-card p-3.5 flex flex-wrap items-center gap-3">
                  <CryptoLogo base={row.symbol.replace("USDT", "")} size={26} />
                  <div className="leading-tight">
                    <div className="font-bold text-[13px]">{row.symbol} <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>{row.interval?.toUpperCase()}</span></div>
                    <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>{p.datePlan} · closed {row.closedAt ? new Date(row.closedAt).toLocaleString() : ""}</div>
                  </div>
                  <span className="pill" style={{ color: row.direction === "LONG" ? "var(--green)" : "var(--red)", background: "transparent", borderColor: "var(--border)" }}>{row.direction}</span>
                  <span className="pill" style={{ color: oColor, background: "var(--panel-2)", borderColor: "var(--border)" }}>{oc}{row.hitLevel ? ` · TP${row.hitLevel}` : ""}</span>
                  <span className="ml-auto tabnum font-extrabold text-[14px]" style={{ color: (row.rMultiple ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{(row.rMultiple ?? 0) >= 0 ? "+" : ""}{row.rMultiple}R</span>
                  <span className="text-[11px] tabnum" style={{ color: "var(--text-muted)" }}>@ {fmtPrice(row.resultPrice)}</span>
                  <button onClick={() => del(row.id)} className="grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "var(--panel-2)", border: "1px solid var(--border)" }}><Trash2 size={12} style={{ color: "var(--red)" }} /></button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold ym-hover"
      style={{ background: active ? "rgba(139,92,246,.25)" : "transparent", color: active ? "#fff" : "var(--text-muted)" }}>
      <Icon size={13} /> {label}
    </button>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="ym-card p-3.5">
      <div className="text-[10px] font-semibold tracking-wide mb-1" style={{ color: "var(--text-dim)" }}>{label.toUpperCase()}</div>
      <div className="text-[20px] font-extrabold tabnum" style={{ color: color || "var(--text)" }}>{value}</div>
    </div>
  );
}

function Countdown({ ts, until }) {
  const [t, setT] = useState(fmtCountdown(ts));
  useEffect(() => { const i = setInterval(() => setT(fmtCountdown(ts)), 1000); return () => clearInterval(i); }, [ts]);
  const expired = t === "EXPIRED";
  return (
    <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <Clock size={12} style={{ color: expired ? "var(--red)" : "var(--accent-soft)" }} />
      <span style={{ color: "var(--text-muted)" }}>Valid until {until}</span>
      <span className="ml-auto tabnum font-bold" style={{ color: expired ? "var(--red)" : "var(--green)" }}>{t}</span>
    </div>
  );
}

function KV({ k, v, c }) {
  return <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>{k}</span><span className="tabnum font-semibold" style={{ color: c || "var(--text)" }}>{v}</span></div>;
}
